import React, { useEffect, useState } from "react";
import { api, fmtDate, formatErr, today, resolveImage } from "@/lib/api";
import { Link } from "react-router-dom";
import { PageHeader, Card, Kpi, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Boxes, AlertTriangle, PackageX, ArrowDown, ArrowUp, Plus, Trash2, BookOpen, Flame, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const TXN_TYPES = ["Opening Stock", "Purchase Entry", "Production Entry", "Return Stock", "Damaged Stock", "Expired Stock", "Manual Adjustment"];
const SPOILAGE_REASONS = [
  "Packet Leaked / Punctured",
  "Product Sourced Expired",
  "Curdling / Sour Milk",
  "Storage Cold-Chain Failure",
  "Transit / Transport Damage",
  "Seal Broken / Packaging Defect",
  "Customer Return Spoilage",
  "Other Quality Rejection"
];

export default function Inventory() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [open, setOpen] = useState(false);
  const [spoilageOpen, setSpoilageOpen] = useState(false);

  const [form, setForm] = useState({ product_id: "", transaction_type: "Purchase Entry", quantity: "", transaction_date: today(), reference_no: "", remarks: "" });
  const [spoilageForm, setSpoilageForm] = useState({
    product_id: "",
    transaction_type: "Damaged Stock",
    quantity: "",
    transaction_date: today(),
    reason: SPOILAGE_REASONS[0],
    remarks: ""
  });

  const [filterDate, setFilterDate] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'spoilage'

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

  let filteredTxns = txns;
  if (filterDate) {
    filteredTxns = filteredTxns.filter(t => t.transaction_date?.split("T")[0] === filterDate);
  }
  if (activeTab === "spoilage") {
    filteredTxns = filteredTxns.filter(t => t.transaction_type === "Damaged Stock" || t.transaction_type === "Expired Stock");
  }

  function load() {
    api.get("/inventory/dashboard").then(r => setStats(r.data));
    api.get("/products").then(r => setProducts(r.data));
    api.get("/inventory/transactions").then(r => setTxns(r.data));
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.product_id) return toast.error("Select product");
    if (!Number(form.quantity)) return toast.error("Enter quantity");
    try {
      await api.post("/inventory/transactions", { ...form, quantity: Number(form.quantity) });
      toast.success("Transaction recorded");
      setOpen(false);
      setForm({ ...form, quantity: "", reference_no: "", remarks: "" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function saveSpoilage() {
    if (!spoilageForm.product_id) return toast.error("Select damaged/expired product");
    if (!Number(spoilageForm.quantity) || Number(spoilageForm.quantity) <= 0) return toast.error("Enter valid quantity");
    
    const combinedRemarks = `Reason: ${spoilageForm.reason}${spoilageForm.remarks ? ` | ${spoilageForm.remarks}` : ""}`;
    try {
      await api.post("/inventory/transactions", {
        product_id: spoilageForm.product_id,
        transaction_type: spoilageForm.transaction_type,
        quantity: Number(spoilageForm.quantity),
        transaction_date: spoilageForm.transaction_date,
        reference_no: "WST-LOG",
        remarks: combinedRemarks,
      });
      toast.success(`Logged ${spoilageForm.transaction_type} entry! Stock updated.`);
      setSpoilageOpen(false);
      setSpoilageForm({
        product_id: "",
        transaction_type: "Damaged Stock",
        quantity: "",
        transaction_date: today(),
        reason: SPOILAGE_REASONS[0],
        remarks: ""
      });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function remove(t) {
    if (!window.confirm("Delete this transaction?")) return;
    try { await api.delete(`/inventory/transactions/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  const currentTransactions = filteredTxns.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredTxns.length / recordsPerPage) || 1;

  return (
    <div data-testid="inventory-page">
      <PageHeader
        eyebrow="Stock Operations"
        title="Inventory & Spoilage Log"
        description="Opening stock, purchases, production, returns, damaged/leaked packets, expiries, and adjustments."
        actions={
          <div className="flex gap-2">
            {/* Log Wastage Modal */}
            <Dialog open={spoilageOpen} onOpenChange={setSpoilageOpen}>
              <DialogTrigger asChild>
                <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="spoilage-log-btn">
                  <Flame size={16} className="mr-1.5" /> Log Wastage / Damage
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-rose-700">
                    <ShieldAlert size={18} /> Log Wastage & Spoilage
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-2">
                  <div className="col-span-2">
                    <Label>Product Affected</Label>
                    <Select value={spoilageForm.product_id} onValueChange={(v)=>setSpoilageForm({...spoilageForm, product_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select damaged or expired item"/></SelectTrigger>
                      <SelectContent>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.product_name} (/{p.unit}) — Current Stock: {p.current_stock ?? 0}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Spoilage Type</Label>
                    <Select value={spoilageForm.transaction_type} onValueChange={(v)=>setSpoilageForm({...spoilageForm, transaction_type: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Damaged Stock">Damaged / Leaked</SelectItem>
                        <SelectItem value="Expired Stock">Expired Packet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={spoilageForm.transaction_date} onChange={e=>setSpoilageForm({...spoilageForm, transaction_date: e.target.value})}/>
                  </div>

                  <div>
                    <Label>Wasted Quantity</Label>
                    <Input type="number" step="0.01" placeholder="e.g. 5" value={spoilageForm.quantity} onChange={e=>setSpoilageForm({...spoilageForm, quantity: e.target.value})}/>
                  </div>

                  <div>
                    <Label>Reason / Category</Label>
                    <Select value={spoilageForm.reason} onValueChange={(v)=>setSpoilageForm({...spoilageForm, reason: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {SPOILAGE_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="col-span-2">
                    <Label>Additional Notes (Optional)</Label>
                    <Textarea rows={2} placeholder="e.g. Batch #402 leaked during morning delivery route" value={spoilageForm.remarks} onChange={e=>setSpoilageForm({...spoilageForm, remarks: e.target.value})}/>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setSpoilageOpen(false)}>Cancel</Button>
                  <Button onClick={saveSpoilage} className="bg-rose-600 hover:bg-rose-700 text-white">Record Spoilage</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Standard New Transaction Modal */}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="inv-create-btn"><Plus size={16} className="mr-1"/>New Transaction</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Inventory Transaction</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Product</Label>
                    <Select value={form.product_id} onValueChange={(v)=>setForm({...form, product_id: v})}>
                      <SelectTrigger data-testid="inv-product"><SelectValue placeholder="Select product"/></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.product_name} (/{p.unit}) — stock {p.current_stock ?? 0}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.transaction_type} onValueChange={(v)=>setForm({...form, transaction_type: v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>{TXN_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={form.transaction_date} onChange={e=>setForm({...form, transaction_date: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Quantity {form.transaction_type === "Manual Adjustment" && <span className="text-xs text-slate-500">(negative for outward)</span>}</Label>
                    <Input type="number" step="0.001" data-testid="inv-qty" value={form.quantity} onChange={e=>setForm({...form, quantity: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Reference</Label>
                    <Input value={form.reference_no} onChange={e=>setForm({...form, reference_no: e.target.value})} placeholder="Bill no / PO / GRN"/>
                  </div>
                  <div className="col-span-2">
                    <Label>Remarks</Label>
                    <Textarea rows={2} value={form.remarks} onChange={e=>setForm({...form, remarks: e.target.value})}/>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button onClick={save} className="bg-cobalt-500 hover:bg-cobalt-700" data-testid="inv-save-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Kpi testid="inv-kpi-products" label="Total Products" value={stats.total_products} icon={Boxes} accent="cobalt"/>
          <Kpi testid="inv-kpi-stock-qty" label="Total Stock Qty" value={Number(stats.total_stock_qty).toFixed(2)} icon={Boxes} accent="slate" hint="Sum across products"/>
          <Kpi testid="inv-kpi-inward" label="Today Inward" value={Number(stats.today_inward).toFixed(2)} icon={ArrowDown} accent="green"/>
          <Kpi testid="inv-kpi-outward" label="Today Outward" value={Number(stats.today_outward).toFixed(2)} icon={ArrowUp} accent="rose"/>
          <Kpi testid="inv-kpi-spoilage" label="Total Wastage" value={Number(stats.total_spoilage_qty || 0).toFixed(2)} icon={Flame} accent="rose" hint={`${stats.total_spoilage_count || 0} incidents`}/>
          <Kpi testid="inv-kpi-low" label="Low / Out Stock" value={stats.low_stock_count + stats.out_of_stock_count} icon={AlertTriangle} accent="amber"/>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="lg:col-span-1" testid="low-stock-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-600"/>
            <h3 className="font-heading font-semibold text-slate-900">Low Stock Alerts</h3>
          </div>
          {(stats?.low_stock_items?.length || 0) === 0 && (stats?.out_of_stock_items?.length || 0) === 0 ? (
            <div className="text-sm text-slate-500">All products above minimum threshold.</div>
          ) : (
            <ul className="space-y-2">
              {stats?.out_of_stock_items?.map(p => (
                <li key={p.id} className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                  <span className="font-medium">{p.product_name}</span>
                  <span className="text-rose-600 font-mono text-xs font-semibold">OUT</span>
                </li>
              ))}
              {stats?.low_stock_items?.map(p => (
                <li key={p.id} className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                  <span className="font-medium">{p.product_name}</span>
                  <span className="text-amber-700 font-mono text-xs">{p.current_stock}/{p.min_stock_threshold} {p.unit}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="lg:col-span-2" testid="product-stock-card">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Current Stock</h3>
          <div className="overflow-x-auto">
            <table className="ddms-table">
              <thead><tr><th></th><th>Product</th><th>Unit</th><th className="num">Current</th><th className="num">Min</th><th></th></tr></thead>
              <tbody>
                {products.map(p => {
                  const stock = Number(p.current_stock || 0);
                  const min = Number(p.min_stock_threshold || 0);
                  const danger = stock <= 0;
                  const warn = !danger && min > 0 && stock <= min;
                  return (
                    <tr key={p.id}>
                      <td>
                        {p.image_url ? <img src={resolveImage(p.image_url)} alt="" className="w-8 h-8 rounded object-cover border border-slate-200"/> : <div className="w-8 h-8 rounded border border-dashed border-slate-300 bg-slate-50"/>}
                      </td>
                      <td className="font-medium">{p.product_name}</td>
                      <td>{p.unit}</td>
                      <td className={`num font-mono font-semibold ${danger ? "text-rose-600" : warn ? "text-amber-600" : "text-slate-900"}`}>{stock.toFixed(2)}</td>
                      <td className="num text-slate-500 font-mono">{min.toFixed(2)}</td>
                      <td className="num"><Link to={`/inventory/product/${p.id}`}><Button size="sm" variant="outline" data-testid={`inv-ledger-${p.product_code}`}><BookOpen size={12}/></Button></Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card padding="p-0" className="mt-4">
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="font-heading font-semibold text-slate-900">
              Inventory Logs & Transactions
            </div>
            <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 text-xs">
              <button
                onClick={() => { setActiveTab("all"); setCurrentPage(1); }}
                className={`px-2.5 py-1 rounded-md font-medium transition-colors ${activeTab === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
              >
                All Logs ({txns.length})
              </button>
              <button
                onClick={() => { setActiveTab("spoilage"); setCurrentPage(1); }}
                className={`px-2.5 py-1 rounded-md font-medium transition-colors flex items-center gap-1 ${activeTab === "spoilage" ? "bg-rose-50 text-rose-700 font-semibold shadow-sm" : "text-slate-500 hover:text-rose-700"}`}
              >
                <Flame size={12} className="text-rose-600" />
                Spoilage Log ({txns.filter(t => t.transaction_type === "Damaged Stock" || t.transaction_type === "Expired Stock").length})
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-auto"
            />

            {filterDate && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilterDate("")}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {filteredTxns.length === 0 ? <div className="p-8"><Empty title={activeTab === "spoilage" ? "No spoilage or damage records logged" : "No transactions found"}/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="inv-txn-table">
              <thead><tr><th>Date</th><th>Product</th><th>Type</th><th>Reference</th><th className="num">In</th><th className="num">Out</th><th className="num">Balance</th><th>Reason / Remarks</th><th className="num"></th></tr></thead>
              <tbody>
                {currentTransactions.map(t => {
                  const isSpoilage = t.transaction_type === "Damaged Stock" || t.transaction_type === "Expired Stock";
                  return (
                    <tr key={t.id} className={isSpoilage ? "bg-rose-50/20" : ""}>
                      <td className="font-mono text-xs">{fmtDate(t.transaction_date)}</td>
                      <td className="font-medium">{t.product_name}</td>
                      <td>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          isSpoilage
                            ? "bg-rose-100 text-rose-800 font-semibold border border-rose-200"
                            : t.transaction_type === "Sales Order Deduction"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-cobalt-50 text-cobalt-700"
                        }`}>
                          {t.transaction_type}
                        </span>
                      </td>
                      <td className="font-mono text-xs font-medium text-slate-600">{t.reference_no}</td>
                      <td className="num text-emerald-600 font-mono">{t.in_qty > 0 ? Number(t.in_qty).toFixed(2) : "—"}</td>
                      <td className="num text-rose-600 font-mono font-semibold">{t.out_qty > 0 ? Number(t.out_qty).toFixed(2) : "—"}</td>
                      <td className="num font-semibold font-mono">{Number(t.balance_qty).toFixed(2)}</td>
                      <td className="text-slate-600">{t.remarks}</td>
                      <td className="num">{t.transaction_type !== "Sales Order Deduction" && (
                        <Button size="sm" variant="outline" onClick={()=>remove(t)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={12}/></Button>
                      )}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between p-4 border-t">
  <span className="text-sm text-slate-500">
    Page {currentPage} of {totalPages}
  </span>

  <div className="flex gap-2">
    <Button
      variant="outline"
      disabled={currentPage === 1}
      onClick={() => setCurrentPage(currentPage - 1)}
    >
      Previous
    </Button>

    <Button
      variant="outline"
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage(currentPage + 1)}
    >
      Next
    </Button>
  </div>
</div>
          </div>
        )}
      </Card>
    </div>
  );
}
